import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { IndianRupee, Filter, ChevronLeft, ChevronRight, Gift, Repeat, Target, Users, TrendingUp } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';

const Earnings = () => {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState('all');
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchEarnings = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/user/earnings.php?page=${currentPage}&limit=${itemsPerPage}&type=${filterType}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          setEarnings(data.data || []);
          setPagination(data.pagination);
        }
      } catch (error) {
        console.error("Failed to fetch earnings", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEarnings();
  }, [currentPage, filterType]);

  const getEarningIcon = (type) => {
    const iconMap = {
      direct_bonus: <Gift className="text-yellow-600" size={20} />,
      direct_income: <IndianRupee className="text-yellow-600" size={20} />,
      matching_bonus: <Users className="text-purple-600" size={20} />,
      pv_match_bonus: <TrendingUp className="text-green-600" size={20} />,
      binary_matching: <Repeat className="text-blue-600" size={20} />,
      binary: <Repeat className="text-blue-600" size={20} />,
      performance_bonus: <Target className="text-pink-600" size={20} />,
      franchise_bonus: <Gift className="text-indigo-600" size={20} />,
      shopping_credit: <Gift className="text-pink-600" size={20} />,
    };
    return iconMap[type] || <IndianRupee className="text-gray-600" size={20} />;
  };

  const formatEarningType = (type) => {
    if (!type) return 'Bonus';
    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Earnings</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">ID: {user?.user_id || ''}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800">Earnings Breakdown</h3>
            <div className="flex items-center space-x-2">
              <Filter size={18} className="text-gray-400 sm:w-5 sm:h-5" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 sm:px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="all">All Earnings</option>
                <option value="direct_bonus">Direct Bonus</option>
                <option value="matching_bonus">Matching Bonus</option>
                <option value="binary">Binary Matching</option>
                <option value="performance_bonus">Performance Bonus</option>
                <option value="franchise_bonus">Franchise Bonus</option>
                <option value="shopping_credit">Shopping Wallet</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : earnings.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <IndianRupee size={48} className="mb-4 opacity-50" />
              <p className="text-lg">No earnings found</p>
              <p className="text-sm">Your earnings will appear here</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="grid grid-cols-1 gap-4 lg:hidden">
                {earnings.map((earning) => (
                  <div key={earning.id} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col gap-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-3">
                        <div className="bg-gray-50 p-2 rounded-lg shrink-0">
                          {getEarningIcon(earning.type)}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{formatEarningType(earning.type)}</p>
                          <p className="text-xs text-gray-500 font-medium">
                            {earning.created_at ? new Date(earning.created_at).toLocaleDateString('en-IN', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            }) : '-'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-black text-emerald-600">
                          ₹{parseFloat(earning.amount || 0).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>

                    {(earning.from_user_name || earning.description) && (
                      <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                        <p className="text-xs text-gray-600">
                          {earning.from_user_name ? (
                            <span>From: <strong className="text-gray-900">{earning.from_user_name}</strong></span>
                          ) : (
                            <span className="italic">{earning.description}</span>
                          )}
                        </p>
                      </div>
                    )}

                    <div className="flex justify-between items-center mt-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</p>
                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${earning.status === 'credited'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-amber-50 text-amber-700'
                        }`}>
                        {earning.status === 'credited' ? 'Credited' : (earning.status || 'Pending')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-4 px-4 text-sm font-bold text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="text-left py-4 px-4 text-sm font-bold text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="text-left py-4 px-4 text-sm font-bold text-gray-500 uppercase tracking-wider">Details</th>
                      <th className="text-right py-4 px-4 text-sm font-bold text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="text-center py-4 px-4 text-sm font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {earnings.map((earning) => (
                      <tr key={earning.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 px-4 text-sm text-gray-600 font-medium">
                          {earning.created_at ? new Date(earning.created_at).toLocaleDateString('en-IN', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          }) : '-'}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-3">
                            <div className="bg-gray-50 p-2 rounded-lg shrink-0">
                              {getEarningIcon(earning.type)}
                            </div>
                            <div className="min-w-0">
                              <span className="text-sm font-bold text-gray-800 block truncate">
                                {formatEarningType(earning.type)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600">
                          {earning.from_user_name ? (
                            <span>From: <strong>{earning.from_user_name}</strong></span>
                          ) : earning.description ? (
                            <span className="text-gray-500 italic">{earning.description}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="text-lg font-black text-emerald-600">
                            ₹{parseFloat(earning.amount || 0).toLocaleString('en-IN')}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${earning.status === 'credited'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                            : 'bg-amber-50 text-amber-700 border border-amber-100'
                            }`}>
                            {earning.status === 'credited' ? 'Credited' : (earning.status || 'Pending')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mt-6">
                <p className="text-sm text-gray-600">
                  Page {pagination.current_page} of {pagination.total_pages}
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  {[...Array(Math.min(pagination.total_pages, 3))].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-4 py-2 rounded-lg ${pagination.current_page === i + 1
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.total_pages}
                    className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Earnings;
